import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setLoader, setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { 
  getConnectionStatus, 
  initiateConnection, 
  disconnectConnector,
  getCalendarList
} from '../../utils/connectors/connectorsService';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';

const GoogleConnector = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modalConfig, showConfirm, closeModal } = useModal();
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionData, setConnectionData] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Check Google Calendar connection status (unified Google integration)
      const status = await getConnectionStatus('googleCalendar', token);
      
      setIsConnected(status.connected);
      setConnectionData(status.data || null);
      
      if (status.connected && status.data) {
        // Load calendar list
        await loadCalendars(token);
      }
      
    } catch (error) {
      console.error('Error checking connection status:', error);
      dispatch(setErrorAlert('Failed to load connection status'));
    } finally {
      setLoading(false);
    }
  };

  const loadCalendars = async (token) => {
    try {
      const calendarList = await getCalendarList(token);
      setCalendars(calendarList || []);
    } catch (error) {
      console.error('Error loading calendars:', error);
      // Non-fatal error, just log it
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      dispatch(setLoader(true));
      
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      // Use googleCalendar connector ID for now (unified OAuth will handle both Calendar and Gmail)
      const authUrl = await initiateConnection('googleCalendar', token);
      
      if (authUrl) {
        // Redirect to Google OAuth
        window.location.href = authUrl;
      } else {
        dispatch(setErrorAlert('Failed to initiate connection'));
      }
      
    } catch (error) {
      console.error('Error connecting:', error);
      dispatch(setErrorAlert('Failed to connect to Google'));
      setIsConnecting(false);
      dispatch(setLoader(false));
    }
  };

  const handleDisconnect = async () => {
    showConfirm(
      'Disconnect Google',
      'Are you sure you want to disconnect Google? This will remove all saved settings for Calendar and Gmail.',
      async () => {
        try {
          setIsDisconnecting(true);
          dispatch(setLoader(true));
          
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          await disconnectConnector('googleCalendar', token);
          
          setIsConnected(false);
          setConnectionData(null);
          setCalendars([]);
          
          dispatch(setSuccessAlert('Google disconnected successfully'));
          
        } catch (error) {
          console.error('Error disconnecting:', error);
          dispatch(setErrorAlert('Failed to disconnect Google'));
        } finally {
          setIsDisconnecting(false);
          dispatch(setLoader(false));
        }
      },
      'warning',
      'Disconnect',
      'Cancel'
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Google</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/connectors')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white text-[12px] opacity-50">Loading...</p>
              </div>
            ) : (
              <>
                {/* Description */}
                <div className="mb-6">
                  <p className="text-white text-[12px] opacity-70 leading-relaxed">
                    Connect your Google account to access Calendar and Gmail features all in one easy integration. 
                    Sync events, track emails, and manage everything from Dizee.
                  </p>
                </div>

                {/* Connection Status Card */}
                <div className="mb-6 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-white text-[12px] font-medium">Connection Status</p>
                    {isConnected ? (
                      <div className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-30 rounded-full px-3 py-1.5">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-green-500 text-[10px] font-medium">Connected</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 bg-red-500 bg-opacity-20 border border-red-500 border-opacity-30 rounded-full px-3 py-1.5">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-red-500 text-[10px] font-medium">Not Connected</span>
                      </div>
                    )}
                  </div>

                  {isConnected && connectionData ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.05)]">
                        <span className="text-white text-[11px] opacity-50">Account</span>
                        <span className="text-white text-[11px]">{connectionData.email || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.05)]">
                        <span className="text-white text-[11px] opacity-50">Connected On</span>
                        <span className="text-white text-[11px]">
                          {formatDate(connectionData.connectedAt)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-[rgba(255,255,255,0.05)]">
                        <span className="text-white text-[11px] opacity-50">Calendars</span>
                        <span className="text-white text-[11px]">{calendars.length} available</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white text-[11px] opacity-50">Services</span>
                        <span className="text-white text-[11px]">Calendar & Gmail</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-white text-[11px] opacity-50">
                      Connect your Google account to enable Calendar sync and Gmail email tracking in one unified integration.
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                {isConnected ? (
                  <div className="mb-6">
                    <button
                      onClick={handleDisconnect}
                      disabled={isDisconnecting}
                      className="w-full bg-[#FF3B30] rounded-lg p-4 hover:bg-[#e63329] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isDisconnecting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-white text-[12px] font-medium">Disconnecting...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span className="text-white text-[12px] font-medium">Disconnect Google</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="w-full bg-[#4285F4] rounded-lg p-4 hover:bg-[#357AE8] transition-all flex items-center justify-center gap-2 mb-6 disabled:opacity-50"
                  >
                    {isConnecting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-white text-[12px] font-medium">Connecting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" viewBox="0 0 488 512" fill="white">
                          <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h240z"/>
                        </svg>
                        <span className="text-white text-[12px] font-medium">Connect Google</span>
                      </>
                    )}
                  </button>
                )}

                {/* Calendar List (if connected) */}
                {isConnected && calendars.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white text-[12px] font-medium mb-3">Your Calendars</h3>
                    <div className="space-y-2">
                      {calendars.map((calendar) => (
                        <div
                          key={calendar.id}
                          className="p-3 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: calendar.backgroundColor || '#4285F4' }}
                            />
                            <div className="flex-1">
                              <p className="text-white text-[11px] font-medium">
                                {calendar.summary}
                              </p>
                              {calendar.description && (
                                <p className="text-white text-[9px] opacity-40 mt-0.5">
                                  {calendar.description}
                                </p>
                              )}
                            </div>
                            {calendar.primary && (
                              <span className="text-[#4285F4] text-[8px] px-2 py-0.5 bg-[rgba(66,133,244,0.1)] border border-[rgba(66,133,244,0.3)] rounded-full">
                                PRIMARY
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features Info */}
                <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] rounded-lg mb-6">
                  <p className="text-white text-[12px] font-medium mb-3">What You Get</p>
                  <div className="space-y-2">
                    {[
                      'Calendar: Create events and sync booking dates',
                      'Calendar: View upcoming events in Dizee',
                      'Gmail: Track important emails automatically',
                      'Gmail: AI-powered email analysis and insights',
                      'Unified: Manage everything in one place'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-green-500 text-[10px] mt-0.5">✓</span>
                        <p className="text-white text-[10px] opacity-70">{feature}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Privacy Note */}
                <div className="p-4 bg-[rgba(255,215,0,0.05)] border border-[rgba(255,215,0,0.2)] rounded-lg">
                  <p className="text-white text-[10px] opacity-70 leading-relaxed">
                    <span className="font-semibold opacity-90">Privacy:</span> Dizee only accesses 
                    your Google Calendar and Gmail to provide these features. We never share your 
                    data with third parties. You can disconnect at any time.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default GoogleConnector;

